// src/pages/Dashboard.jsx
// Brutalist dashboard with real operational data
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import {
    DocumentTextIcon,
    PlayIcon,
    ClockIcon,
    UsersIcon,
    BuildingOfficeIcon,
    ServerIcon,
    CpuChipIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon,
    GlobeAltIcon,
    BoltIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const Dashboard = () => {
    const { executeQuery, queries, loading, error } = useGraphQL();
    const [stats, setStats] = useState(null);
    const [jobStats, setJobStats] = useState(null);
    const [workflowStats, setWorkflowStats] = useState(null);
    const [deepCrawlStats, setDeepCrawlStats] = useState(null);
    const [entityCounts, setEntityCounts] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [cpvTree, setCpvTree] = useState(null);
    const [marketSignals, setMarketSignals] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        loadAllData();
    }, []);

    const loadAllData = async () => {
        await Promise.all([
            loadStats(),
            loadJobStats(),
            loadWorkflowStats(),
            loadDeepCrawlStats(),
            loadEntityCounts(),
            loadSystemHealth(),
            loadCpvTree(),
            loadMarketSignals()
        ]);
    };

    // Global market signals computed from narrative profiles (taxonomy tags,
    // framework/local-presence rates, stated budgets). Corpus-wide, not
    // company-scoped.
    const loadMarketSignals = async () => {
        try {
            const timeRange = { preset: 'last_30_days' };
            const [statsRes, tagsRes, budgetRes] = await Promise.all([
                executeQuery(queries.getNarrativeStats(), { timeRange }),
                executeQuery(queries.getTopTags(), { timeRange, limit: 14 }),
                executeQuery(queries.getBudgetByDomain(), { timeRange, limit: 8 }),
            ]);
            setMarketSignals({
                stats: statsRes.narrativeStats,
                topTags: tagsRes.topTags || [],
                budgetByDomain: budgetRes.budgetByDomain || [],
            });
        } catch (err) {
            console.error('Failed to load market signals:', err);
        }
    };

    const loadStats = async () => {
        try {
            const data = await executeQuery(queries.getStats());
            setStats(data.stats);
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    };

    const loadJobStats = async () => {
        try {
            // Load jobs for each status to get counts
            const [running, pending, completed, failed] = await Promise.all([
                executeQuery(queries.getJobs(), { status: 'RUNNING', limit: 100, isScheduled: false }),
                executeQuery(queries.getJobs(), { status: 'PENDING', limit: 100, isScheduled: false }),
                executeQuery(queries.getJobs(), { status: 'COMPLETED', limit: 50, isScheduled: false }),
                executeQuery(queries.getJobs(), { status: 'FAILED', limit: 50, isScheduled: false })
            ]);

            // Count jobs by type for the running/pending jobs
            const jobTypeCounts = {};
            [...(running.adminJobs || []), ...(pending.adminJobs || [])].forEach(job => {
                jobTypeCounts[job.type] = (jobTypeCounts[job.type] || 0) + 1;
            });

            setJobStats({
                running: running.adminJobs?.length || 0,
                pending: pending.adminJobs?.length || 0,
                completedRecent: completed.adminJobs?.length || 0,
                failedRecent: failed.adminJobs?.length || 0,
                jobTypeCounts,
                recentFailed: (failed.adminJobs || []).slice(0, 5)
            });
        } catch (err) {
            console.error('Failed to load job stats:', err);
        }
    };

    const loadWorkflowStats = async () => {
        try {
            // Load all workflow runs without status filter first, then filter client-side
            // This avoids potential enum issues with the status filter
            const allRuns = await executeQuery(queries.getWorkflowRuns(null, null, 100, 0));
            const runs = allRuns.workflowRuns || [];

            const running = runs.filter(r => r.status === 'RUNNING');
            const pending = runs.filter(r => r.status === 'PENDING');
            const completed = runs.filter(r => r.status === 'COMPLETED');
            const failed = runs.filter(r => r.status === 'FAILED');

            setWorkflowStats({
                running: running.length,
                pending: pending.length,
                completedRecent: completed.slice(0, 20).length,
                failedRecent: failed.slice(0, 20).length,
                recentRuns: [
                    ...running,
                    ...completed.slice(0, 3),
                    ...failed.slice(0, 2)
                ].slice(0, 5)
            });
        } catch (err) {
            console.error('Failed to load workflow stats:', err);
            // Set empty stats on failure
            setWorkflowStats({
                running: 0,
                pending: 0,
                completedRecent: 0,
                failedRecent: 0,
                recentRuns: []
            });
        }
    };

    const loadDeepCrawlStats = async () => {
        try {
            const data = await executeQuery(queries.getDeepCrawlStats());
            setDeepCrawlStats(data.deepCrawlStats);
        } catch (err) {
            console.error('Failed to load deep crawl stats:', err);
        }
    };

    const loadEntityCounts = async () => {
        try {
            // Fetch companies and users
            const [companiesData, usersData] = await Promise.all([
                executeQuery(queries.getCompanies(), { limit: 1000, offset: 0 }),
                executeQuery(queries.getUsers(), { limit: 1000, offset: 0 })
            ]);

            // Use lighter sessions query that avoids impersonatedBy field
            // (can fail if impersonating admin was deleted - FK sets null but resolver may expect non-null)
            let sessionCount = 0;
            try {
                const sessionsData = await executeQuery(queries.getSessionsBasic(), { activeOnly: true, limit: 1000 });
                sessionCount = sessionsData.adminSessions?.length || 0;
            } catch (sessionErr) {
                console.warn('Failed to load sessions:', sessionErr);
            }

            setEntityCounts({
                companies: companiesData.adminCompanies?.length || 0,
                users: usersData.adminUsers?.length || 0,
                activeSessions: sessionCount
            });
        } catch (err) {
            console.error('Failed to load entity counts:', err);
        }
    };

    const loadSystemHealth = async () => {
        try {
            const data = await executeQuery(queries.getSystemHealth());
            setSystemHealth(data.systemHealth);
        } catch (err) {
            console.error('Failed to load system health:', err);
        }
    };

    const loadCpvTree = async () => {
        try {
            const data = await executeQuery(queries.getCpvTree(), { input: {} });
            setCpvTree(data.cpvTree);
        } catch (err) {
            console.error('Failed to load CPV tree:', err);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await loadAllData();
        } finally {
            setRefreshing(false);
        }
    };

    // Prepare job type distribution chart data
    const jobTypeChartData = jobStats?.jobTypeCounts
        ? Object.entries(jobStats.jobTypeCounts)
            .map(([type, count]) => ({ type: type.replace(/_/g, ' '), count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8)
        : [];

    // Prepare job status pie chart data
    const jobStatusData = jobStats ? [
        { name: 'Running', value: jobStats.running, color: '#00BB8F' },
        { name: 'Pending', value: jobStats.pending, color: '#FFC107' },
        { name: 'Completed', value: jobStats.completedRecent, color: '#4897C3' },
        { name: 'Failed', value: jobStats.failedRecent, color: '#FF0092' }
    ].filter(d => d.value > 0) : [];

    // Deep crawl domains chart data - use topDomainsByTenderCount to prioritize important domains
    const topDomainsData = deepCrawlStats?.topDomainsByTenderCount?.slice(0, 8).map(d => ({
        domain: d.domain.replace(/^www\./, '').slice(0, 25),
        tenders: d.tenderCount,
        successRate: Math.round(d.successRate * 100),
        failures: d.failureCount
    })) || [];

    // CPV distribution data - top level categories
    const cpvChartData = cpvTree?.tree?.slice(0, 10).map(node => ({
        code: node.code.split('-')[0], // Just the number part
        label: node.labelEN.length > 25 ? node.labelEN.slice(0, 25) + '...' : node.labelEN,
        count: node.countTotal
    })).filter(d => d.count > 0) || [];

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-5 h-5 border-2 border-gray-600 border-t-primary animate-spin" />
                <span className="ml-3 font-mono text-xs text-gray-500 uppercase">Loading dashboard...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6 min-h-full">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-lg font-mono font-bold text-white uppercase tracking-tight">SYSTEM DASHBOARD</h1>
                <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="px-4 py-2 font-mono text-xs font-bold text-on-accent bg-primary hover:brightness-110 disabled:opacity-50 uppercase flex items-center gap-2"
                >
                    <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'REFRESHING...' : 'REFRESH'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="font-mono text-xs text-red-700 uppercase">ERROR: {error}</p>
                </div>
            )}

            {/* Row 1: Platform Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="TOTAL TENDERS"
                    value={stats?.totalTenders || 0}
                    subValue={stats?.newTenders || 0}
                    subLabel="new this week"
                    icon={DocumentTextIcon}
                    color="bg-primary"
                />
                <KPICard
                    title="COMPANIES"
                    value={entityCounts?.companies || 0}
                    icon={BuildingOfficeIcon}
                    color="bg-blue-500"
                />
                <KPICard
                    title="USERS"
                    value={entityCounts?.users || 0}
                    icon={UsersIcon}
                    color="bg-purple-500"
                />
                <KPICard
                    title="ACTIVE SESSIONS"
                    value={entityCounts?.activeSessions || 0}
                    icon={BoltIcon}
                    color="bg-amber-500"
                />
            </div>

            {/* Row 2: Job & Processing Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="JOBS RUNNING"
                    value={jobStats?.running || 0}
                    subValue={jobStats?.pending || 0}
                    subLabel="pending"
                    icon={PlayIcon}
                    color="bg-green-500"
                />
                <KPICard
                    title="PENDING CRAWLS"
                    value={stats?.pendingCrawls || 0}
                    icon={GlobeAltIcon}
                    color="bg-cyan-500"
                />
                <KPICard
                    title="PENDING ASSESSMENTS"
                    value={stats?.pendingAssessments || 0}
                    icon={CpuChipIcon}
                    color="bg-indigo-500"
                />
                <KPICard
                    title="WORKFLOWS RUNNING"
                    value={workflowStats?.running || 0}
                    subValue={workflowStats?.pending || 0}
                    subLabel="pending"
                    icon={ArrowPathIcon}
                    color="bg-pink-500"
                />
            </div>

            {/* Row 3: Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Job Status Distribution */}
                <div className="surface-card p-4">
                    <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">
                        JOB STATUS (RECENT)
                    </h3>
                    {jobStatusData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={jobStatusData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                                    outerRadius={70}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {jobStatusData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        fontFamily: 'monospace',
                                        fontSize: '11px'
                                    }}
                                />
                                <Legend wrapperStyle={{ fontFamily: 'monospace', fontSize: '11px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[220px] font-mono text-xs text-gray-600 uppercase">
                            No job data available
                        </div>
                    )}
                </div>

                {/* Active Jobs by Type */}
                <div className="surface-card p-4">
                    <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">
                        ACTIVE JOBS BY TYPE
                    </h3>
                    {jobTypeChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={jobTypeChartData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10, fontFamily: 'monospace' }} />
                                <YAxis dataKey="type" type="category" stroke="#94a3b8" width={100} tick={{ fontSize: 9, fontFamily: 'monospace' }} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        fontFamily: 'monospace',
                                        fontSize: '11px'
                                    }}
                                />
                                <Bar dataKey="count" fill="#00BB8F" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[220px] font-mono text-xs text-gray-600 uppercase">
                            No active jobs
                        </div>
                    )}
                </div>
            </div>

            {/* Row 4: Deep Crawl Stats */}
            {deepCrawlStats && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="surface-card p-4">
                        <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">DEEP CRAWL OVERVIEW</h3>
                        <div className="space-y-3">
                            <StatRow label="Site Configs" value={deepCrawlStats.totalSiteConfigs} />
                            <StatRow label="Active Configs" value={deepCrawlStats.activeSiteConfigs} />
                            <StatRow label="Expired Configs" value={deepCrawlStats.expiredSiteConfigs} />
                            <StatRow
                                label="Recent Success Rate"
                                value={`${(deepCrawlStats.recentSuccessRate * 100).toFixed(1)}%`}
                                highlight={deepCrawlStats.recentSuccessRate > 0.8 ? 'green' : deepCrawlStats.recentSuccessRate > 0.5 ? 'yellow' : 'red'}
                            />
                            <StatRow label="Recent Failures" value={deepCrawlStats.recentFailureCount} highlight={deepCrawlStats.recentFailureCount > 10 ? 'red' : null} />
                        </div>
                    </div>

                    <div className="lg:col-span-2 surface-card p-4">
                        <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">TOP DOMAINS BY TENDER COUNT</h3>
                        {topDomainsData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={topDomainsData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10, fontFamily: 'monospace' }} />
                                    <YAxis dataKey="domain" type="category" stroke="#94a3b8" width={140} tick={{ fontSize: 9, fontFamily: 'monospace' }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            fontFamily: 'monospace',
                                            fontSize: '11px'
                                        }}
                                        formatter={(value, name, props) => {
                                            if (name === 'tenders') {
                                                const item = props.payload;
                                                return [`${value} (${item.successRate}% success, ${item.failures} failures)`, 'Tenders'];
                                            }
                                            return [value, name];
                                        }}
                                    />
                                    <Bar dataKey="tenders" fill="#4897C3" name="tenders" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[180px] font-mono text-xs text-gray-600 uppercase">
                                No domain data available
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Row 5: CPV Distribution */}
            {cpvChartData.length > 0 && (
                <div className="surface-card p-4">
                    <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">
                        TENDERS BY CPV CATEGORY
                        {cpvTree?.totalCount && (
                            <span className="text-gray-500 font-normal ml-2">({cpvTree.totalCount.toLocaleString()} total)</span>
                        )}
                    </h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={cpvChartData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10, fontFamily: 'monospace' }} />
                            <YAxis dataKey="label" type="category" stroke="#94a3b8" width={200} tick={{ fontSize: 9, fontFamily: 'monospace' }} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    fontFamily: 'monospace',
                                    fontSize: '11px'
                                }}
                                formatter={(value, name, props) => {
                                    const item = props.payload;
                                    return [`${value.toLocaleString()} tenders`, `CPV ${item.code}`];
                                }}
                            />
                            <Bar dataKey="count" fill="#00BB8F" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Row 5b: Market signals from narrative profiles (last 30 days) */}
            {marketSignals && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="surface-card p-4">
                        <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">MARKET SIGNALS (30D)</h3>
                        <div className="space-y-3">
                            <StatRow label="Narratives" value={marketSignals.stats.totalNarratives} />
                            <StatRow label="Framework Rate" value={`${(marketSignals.stats.frameworkRate * 100).toFixed(0)}%`} />
                            <StatRow label="Local Presence Req." value={`${(marketSignals.stats.localPresenceRate * 100).toFixed(0)}%`} />
                            <StatRow label="Avg Stated Budget" value={marketSignals.stats.avgBudgetEUR ? `€${Math.round(marketSignals.stats.avgBudgetEUR).toLocaleString('de-DE')}` : '–'} />
                            <StatRow label="Median Stated Budget" value={marketSignals.stats.medianBudgetEUR ? `€${Math.round(marketSignals.stats.medianBudgetEUR).toLocaleString('de-DE')}` : '–'} />
                            <StatRow label="Avg Duration" value={marketSignals.stats.avgDurationMonths ? `${marketSignals.stats.avgDurationMonths.toFixed(1)} mo` : '–'} />
                        </div>
                        {marketSignals.topTags.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-line">
                                <p className="font-mono text-[10px] text-gray-500 uppercase mb-2">Top taxonomy tags</p>
                                <div className="flex flex-wrap gap-1">
                                    {marketSignals.topTags.map(t => (
                                        <span key={t.tag} className="px-1.5 py-0.5 bg-gray-800 border border-gray-700 font-mono text-[10px] text-gray-300">
                                            {t.tag} <span className="text-gray-500">{t.count}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-2 surface-card p-4">
                        <h3 className="font-mono text-xs font-bold text-white uppercase mb-4">STATED BUDGETS BY DOMAIN (30D)</h3>
                        {marketSignals.budgetByDomain.length > 0 ? (
                            <table className="w-full font-mono text-xs">
                                <thead>
                                    <tr className="text-gray-500 uppercase text-[10px]">
                                        <th className="text-left pb-2">Domain</th>
                                        <th className="text-right pb-2">Tenders</th>
                                        <th className="text-right pb-2">Avg Budget</th>
                                        <th className="text-right pb-2">Median Budget</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marketSignals.budgetByDomain.map(row => (
                                        <tr key={row.domain} className="border-t border-line">
                                            <td className="py-1.5 text-white">{row.domain}</td>
                                            <td className="py-1.5 text-right text-gray-300">{row.count}</td>
                                            <td className="py-1.5 text-right text-gray-300">{row.avgBudgetEUR ? `€${Math.round(row.avgBudgetEUR).toLocaleString('de-DE')}` : '–'}</td>
                                            <td className="py-1.5 text-right text-gray-300">{row.medianBudgetEUR ? `€${Math.round(row.medianBudgetEUR).toLocaleString('de-DE')}` : '–'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex items-center justify-center h-[180px] font-mono text-xs text-gray-600 uppercase">
                                No stated budgets in range
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Row 6: Recent Failed Jobs */}
            {jobStats?.recentFailed?.length > 0 && (
                <div className="surface-card p-4">
                    <h3 className="font-mono text-xs font-bold text-white uppercase mb-4 flex items-center gap-2">
                        <ExclamationCircleIcon className="w-4 h-4 text-red-700" />
                        RECENT FAILED JOBS
                    </h3>
                    <div className="space-y-2">
                        {jobStats.recentFailed.map((job) => (
                            <div key={job.id} className="flex items-center justify-between bg-slate-50 rounded-md p-2 border-l-2 border-red-500">
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-xs text-gray-400">{job.type}</span>
                                    {job.tenderId && (
                                        <span className="font-mono text-xs text-gray-500">
                                            Tender: {job.tenderId.slice(0, 8)}...
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-xs text-red-700 max-w-xs truncate">
                                        {job.error || 'Unknown error'}
                                    </span>
                                    <span className="font-mono text-xs text-gray-500">
                                        {job.completedAt ? new Date(job.completedAt).toLocaleString() : '-'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Row 7: System Health */}
            <div className="surface-card p-4">
                <h3 className="font-mono text-xs font-bold text-white uppercase mb-4 flex items-center gap-2">
                    <ServerIcon className="w-4 h-4" />
                    SYSTEM HEALTH
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {systemHealth && systemHealth.length > 0 ? (
                        systemHealth.map((service) => (
                            <HealthCard key={service.name} service={service} />
                        ))
                    ) : (
                        <>
                            <HealthCard service={{ name: 'API Server', status: 'healthy', responseTimeMs: 45 }} />
                            <HealthCard service={{ name: 'Database', status: 'healthy', responseTimeMs: 12 }} />
                            <HealthCard service={{ name: 'Job Worker', status: stats?.activeJobs > 0 ? 'healthy' : 'idle' }} />
                            <HealthCard service={{ name: 'Crawler', status: stats?.pendingCrawls > 0 ? 'busy' : 'idle' }} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// KPI Card Component
const KPICard = ({ title, value, subValue, subLabel, icon: Icon, color }) => {
    return (
        <div className="surface-card p-4">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className={`${color} p-2 rounded-lg`}>
                        <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <p className="font-mono text-xs text-gray-500 uppercase">{title}</p>
                        <p className="font-mono text-xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
                    </div>
                </div>
            </div>
            {(subValue !== null && subValue !== undefined) && (
                <div className="mt-3 pt-3 border-t border-line">
                    <p className="font-mono text-xs text-gray-400">
                        <span className="text-primary font-bold">{subValue}</span>
                        {subLabel && ` ${subLabel}`}
                    </p>
                </div>
            )}
        </div>
    );
};

// Stat Row Component
const StatRow = ({ label, value, highlight }) => {
    const highlightColors = {
        green: 'text-green-700',
        yellow: 'text-yellow-700',
        red: 'text-red-700'
    };

    return (
        <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-gray-400">{label}</span>
            <span className={`font-mono text-sm font-bold ${highlight ? highlightColors[highlight] : 'text-white'}`}>
                {value}
            </span>
        </div>
    );
};

// Health Card Component
const HealthCard = ({ service }) => {
    const statusConfig = {
        healthy: { icon: CheckCircleIcon, color: 'text-green-700', bg: 'bg-green-500/10', label: 'HEALTHY' },
        degraded: { icon: ExclamationCircleIcon, color: 'text-yellow-700', bg: 'bg-yellow-500/10', label: 'DEGRADED' },
        unhealthy: { icon: XCircleIcon, color: 'text-red-700', bg: 'bg-red-500/10', label: 'UNHEALTHY' },
        idle: { icon: ClockIcon, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'IDLE' },
        busy: { icon: CpuChipIcon, color: 'text-blue-700', bg: 'bg-blue-500/10', label: 'BUSY' }
    };

    const config = statusConfig[service.status] || statusConfig.healthy;
    const StatusIcon = config.icon;

    return (
        <div className={`${config.bg} border border-gray-700 p-3`}>
            <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-xs font-bold text-white uppercase">{service.name}</span>
                <StatusIcon className={`w-4 h-4 ${config.color}`} />
            </div>
            <div className="flex items-center justify-between">
                <span className={`font-mono text-xs font-bold ${config.color}`}>{config.label}</span>
                {service.responseTimeMs && (
                    <span className="font-mono text-xs text-gray-500">{service.responseTimeMs}ms</span>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
